import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HubRecordEditorBase } from '../_hub_record_editor_base';
import { ProjectOutcomeModel } from '../../../../hub_schema/hubTypes';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CreateOutcomeDialogComponent } from './create-outcome-dialog/create-outcome-dialog.component';
import { EditOutcomeDialogComponent } from './edit-outcome-dialog/edit-outcome-dialog.component';

@Component({
    selector: 'app-outcomes-edit-container',
    templateUrl: './outcomes-edit-container.component.html',
    styleUrls: ['./outcomes-edit-container.component.scss'],
    standalone: false
})
export class OutcomesEditContainerComponent extends HubRecordEditorBase implements OnInit, OnDestroy {
    private dialogService: MatDialog = inject(MatDialog);

    public outcomes: ProjectOutcomeModel[];
    public customOutcomes: ProjectOutcomeModel[];
    public selectedProjectOutcome: ProjectOutcomeModel | undefined;
    private projectUpdatedSubscription: Subscription | undefined;

    public ngOnInit(): void {
        this.initialize();

        this.projectUpdatedSubscription = this.hubRecordEditService.projectUpdated.subscribe(() => {
            this.resetProjectOutcomeLists();

            if (this.selectedProjectOutcome) {
                const previouslySelectedOutcome = this.outcomes.find(po => this.objectEquals(po, this.selectedProjectOutcome!));
                const previouslySelectedCustomOutcome = this.customOutcomes.find(po => this.objectEquals(po, this.selectedProjectOutcome!));

                if (previouslySelectedOutcome) {
                    this.selectedProjectOutcome = previouslySelectedOutcome;
                }
                else if (previouslySelectedCustomOutcome) {
                    this.selectedProjectOutcome = previouslySelectedCustomOutcome;
                }
            }
        });
    }

    /// used to determine Object Equivalence when a stable reference is not guaranteed
    private objectEquals(projectOutcome1: ProjectOutcomeModel, projectOutcome2: ProjectOutcomeModel): boolean {
        if (!!projectOutcome1.projectOutcomeId && !!projectOutcome2.projectOutcomeId) {
            return projectOutcome1.projectOutcomeId === projectOutcome2.projectOutcomeId;
        }
        return this.getHashCode(projectOutcome1) === this.getHashCode(projectOutcome2);
    }

    private getHashCode(projectOutcome: ProjectOutcomeModel): string {
        if (!!projectOutcome.outcome) {
            return `
                {
                    outcome: {
                        outcomeMetricId: ${projectOutcome.outcome.outcomeMetricId},
                        outcomeUnitId: ${projectOutcome.outcome.outcomeUnitId},
                        statement: "${projectOutcome.statement}"
                    }
                }
            `;
        }
        else {
            return `
                {
                    customOutcome: {
                        indicator: "${projectOutcome.customOutcome?.indicatorName}",
                        unit: "${projectOutcome.customOutcome?.unit}",
                        statement: "${projectOutcome.statement}"
                    }
                }`;
        }

    }

    private get projectOutcomes(): ProjectOutcomeModel[] {
        return this.projectEditForm!.controls.projectOutcomes.value;
    }

    private resetProjectOutcomeLists(): void {
        this.outcomes = this.projectOutcomes.filter(po => po.outcome);
        this.customOutcomes = this.projectOutcomes.filter(po => po.customOutcome);
    }

    private initialize(): void {
        this.resetProjectOutcomeLists();
        this.selectFirstProjectOutcome();
    }

    private selectFirstProjectOutcome() {
        if (this.outcomes.length) {
            this.selectedProjectOutcome = this.outcomes[0];
        }
        else if (this.customOutcomes.length) {
            this.selectedProjectOutcome = this.customOutcomes[0];
        }
        else {
            this.selectedProjectOutcome = undefined;
        }
    }

    public selectProjectOutcome(projectOutcome: ProjectOutcomeModel): void {
        this.selectedProjectOutcome = projectOutcome;
    }

    // #region Create/Edit/Delete

    public openCreateOutcomeDialog(): void {
        const dialogRef = this.dialogService.open(CreateOutcomeDialogComponent, {
            width: '800px',
            maxWidth: 'none',
            data: {
                isStrategy: this.isStrategy,
                projectId: this.project.projectId
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((newProjectOutcome: ProjectOutcomeModel) => {
            if (newProjectOutcome) {
                this.projectOutcomes.push(newProjectOutcome);
                this.projectEditForm!.controls.projectOutcomes.markAsDirty();
                this.resetProjectOutcomeLists();
                this.selectedProjectOutcome = newProjectOutcome;
            }
        });
    }

    public openEditOutcomeDialog(projectOutcome: ProjectOutcomeModel): void {
        const dialogRef = this.dialogService.open(EditOutcomeDialogComponent, {
            width: '800px',
            maxWidth: 'none',
            data: {
                projectOutcome: projectOutcome,
                isStrategy: this.isStrategy
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((updatedProjectOutcome: ProjectOutcomeModel) => {
            if (updatedProjectOutcome) {
                const index = this.projectOutcomes.findIndex(po => po === projectOutcome);
                // preserve lazily-loaded targets/progress
                updatedProjectOutcome.targets = projectOutcome.targets;
                updatedProjectOutcome.progress = projectOutcome.progress;
                this.projectOutcomes[index] = updatedProjectOutcome;
                this.projectEditForm!.controls.projectOutcomes.markAsDirty();
                this.resetProjectOutcomeLists();
                this.selectedProjectOutcome = this.projectOutcomes[index];
            }
        });
    }

    public deleteProjectOutcome(projectOutcome: ProjectOutcomeModel): void {
        const index = this.projectOutcomes.findIndex(po => po === projectOutcome);
        this.projectOutcomes.splice(index, 1);
        this.projectEditForm!.controls.projectOutcomes.markAsDirty();
        this.initialize();
    }

    // #endregion Create/Edit/Delete

    // #region Reordering

    public outcomeReordered(event: CdkDragDrop<ProjectOutcomeModel>): void {
        moveItemInArray(this.outcomes, event.previousIndex, event.currentIndex);
        this.syncOutcomeOrder();
    }

    public customOutcomeReordered(event: CdkDragDrop<ProjectOutcomeModel>): void {
        moveItemInArray(this.customOutcomes, event.previousIndex, event.currentIndex);
        this.syncOutcomeOrder();
    }

    private syncOutcomeOrder(): void {
        this.outcomes.forEach((o, index) => o.sortOrder = index);
        this.customOutcomes.forEach((o, index) => o.sortOrder = index);

        const reordered = [...this.outcomes, ...this.customOutcomes];
        const control = this.projectEditForm!.controls.projectOutcomes;
        control.setValue(reordered);
        control.markAsDirty();
    }

    // #endregion Reordering

    public ngOnDestroy(): void {
        if (this.projectUpdatedSubscription) {
            this.projectUpdatedSubscription.unsubscribe();
        }
    }

}
